import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Close,
  Search,
  GetApp,
  OpenInNew,
  PhoneIphone,
  Android,
  Apple,
  Apps,
} from "@mui/icons-material";
import { useMoreApps } from "../context/MoreAppsContext";

export const MoreAppsModal: React.FC = () => {
  const { apps, loading, isOpen, closeMoreApps } = useMoreApps();
  const [searchQuery, setSearchQuery] = useState("");

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredApps = apps.filter((app) =>
    app.app_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            boxSizing: "border-box",
          }}
        >
          {/* Dark Backdrop Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMoreApps}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.82)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              zIndex: 1,
            }}
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            style={{
              zIndex: 2,
              backgroundColor: "#0d111c",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              boxShadow: "0 25px 80px rgba(0, 0, 0, 0.95)",
              color: "#ffffff",
              width: "100%",
              maxWidth: "1050px",
              height: "84vh",
              maxHeight: "84vh",
              display: "flex",
              flexDirection: "column",
              borderRadius: "24px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Header (Fixed) */}
            <div
              style={{
                flexShrink: 0,
                backgroundColor: "rgba(15, 23, 42, 0.98)",
                padding: "20px 24px",
                borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {/* Close Button X */}
              <button
                onClick={closeMoreApps}
                id="close-more-apps-btn"
                style={{
                  position: "absolute",
                  top: "18px",
                  right: "18px",
                  height: "36px",
                  width: "36px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                  color: "#ffffff",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 10,
                  transition: "background-color 0.2s",
                }}
                aria-label="Close modal"
              >
                <Close sx={{ fontSize: 22 }} />
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: "12px", paddingRight: "40px" }}>
                <div style={{ padding: "10px", borderRadius: "16px", background: "linear-gradient(135deg, #6366f1, #a855f7)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Apps sx={{ fontSize: 26 }} />
                </div>
                <div>
                  <h2 style={{ fontSize: "1.35rem", fontWeight: 800, margin: 0, color: "#ffffff", letterSpacing: "-0.02em" }}>
                    Our Mobile Applications
                  </h2>
                  <p style={{ fontSize: "0.85rem", color: "#94a3b8", margin: "3px 0 0 0" }}>
                    Discover and download our top AI tools and games for Android & iOS
                  </p>
                </div>
              </div>

              {/* Search Bar */}
              <div style={{ position: "relative", marginTop: "4px" }}>
                <Search style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "20px" }} />
                <input
                  type="text"
                  id="more-apps-search-input"
                  placeholder="Search mobile apps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%",
                    paddingLeft: "42px",
                    paddingRight: "16px",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                    borderRadius: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    color: "#ffffff",
                    fontSize: "0.9rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            {/* Scrollable Content Body (5 columns in one row) */}
            <div
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                padding: "20px 24px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <div
                      key={i}
                      className="p-3.5 rounded-2xl bg-white/5 border border-white/10 animate-pulse flex flex-col items-center gap-2"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/10" />
                      <div className="w-full space-y-1.5">
                        <div className="h-3 bg-white/10 rounded w-3/4 mx-auto" />
                        <div className="h-2.5 bg-white/10 rounded w-1/2 mx-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredApps.length > 0 ? (
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3.5"
                >
                  {filteredApps.map((app, idx) => {
                    const androidUrl = app.app_link?.android;
                    const iosUrl = app.app_link?.ios;
                    const mainUrl = androidUrl || iosUrl || "#";

                    return (
                      <motion.div
                        key={idx}
                        whileHover={{ y: -3, scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        style={{
                          padding: "14px",
                          borderRadius: "16px",
                          backgroundColor: "rgba(255, 255, 255, 0.04)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "8px" }}>
                          <img
                            src={app.app_logo}
                            alt={app.app_name}
                            style={{
                              width: "52px",
                              height: "52px",
                              borderRadius: "14px",
                              objectFit: "cover",
                              border: "1px solid rgba(255, 255, 255, 0.15)",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                              flexShrink: 0,
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://snaptoon-development.s3.eu-north-1.amazonaws.com/logo/swaptoon_app_logo.png";
                            }}
                          />
                          <div style={{ width: "100%" }}>
                            <h3
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: 700,
                                margin: 0,
                                color: "#ffffff",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={app.app_name}
                            >
                              {app.app_name}
                            </h3>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", marginTop: "5px" }}>
                              {androidUrl && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "2px 6px", borderRadius: "5px", fontSize: "10px", fontWeight: 600, backgroundColor: "rgba(16, 185, 129, 0.18)", color: "#34d399", border: "1px solid rgba(16, 185, 129, 0.35)" }}>
                                  <Android sx={{ fontSize: 11 }} /> Android
                                </span>
                              )}
                              {iosUrl && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "2px 6px", borderRadius: "5px", fontSize: "10px", fontWeight: 600, backgroundColor: "rgba(56, 189, 248, 0.18)", color: "#38bdf8", border: "1px solid rgba(56, 189, 248, 0.35)" }}>
                                  <Apple sx={{ fontSize: 11 }} /> iOS
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Download CTA */}
                        <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}>
                          <a
                            href={mainUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                              color: "#ffffff",
                              fontWeight: 800,
                              fontSize: "0.78rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              textDecoration: "none",
                              boxSizing: "border-box",
                              boxShadow: "0 3px 12px rgba(99, 102, 241, 0.3)",
                            }}
                          >
                            <GetApp sx={{ fontSize: 14 }} /> Get App <OpenInNew sx={{ fontSize: 11 }} />
                          </a>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
                  <PhoneIphone sx={{ fontSize: 44 }} style={{ marginBottom: "8px", opacity: 0.4 }} />
                  <p style={{ fontSize: "0.9rem", fontWeight: 600, color: "#ffffff", margin: 0 }}>No mobile applications found</p>
                  <p style={{ fontSize: "0.8rem", color: "#94a3b8", marginTop: "4px" }}>Try searching with a different name.</p>
                </div>
              )}
            </div>

            {/* Footer (Fixed) */}
            <div
              style={{
                flexShrink: 0,
                backgroundColor: "rgba(10, 15, 26, 0.98)",
                padding: "14px 24px",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: "0.8rem",
                color: "#94a3b8",
              }}
            >
              <span>{apps.length} Apps Available</span>
              <button
                onClick={closeMoreApps}
                style={{
                  padding: "6px 16px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "#ffffff",
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default MoreAppsModal;
